import { Icon } from '../../../widgets/icon';
import { Input } from '../../../widgets/input';
import { Link } from '../../../widgets/buttons';
import { Row, Table } from '../../../widgets/table';
import { ZTooltip } from '../../../widgets/ztooltip';
import React, { FC, FunctionComponent, useEffect, useRef } from 'react';
import ColorSelection from 'src/components/widgets/colorSelection';
import { PaletteType } from 'src/shared/enums';
import { AddButton, DeleteButton } from 'src/shared/globals';
import { hexToRgb, T } from 'src/shared/helpers';
import ArrowRightSolid from 'src/shared/icons/arrow-right-solid.svg';
import BookOpen from 'src/shared/icons/book-open-solid.svg';
import UndoIcon from 'src/shared/icons/undo-alt-solid.svg';
import styled from 'styled-components';
import { PaletteColorInput } from './__generated__/paletteColorsPageUpdateMutation.graphql';
import { IconButtonGrey } from './colorPaletteSettings';
import { COLUMN_TYPES, ColumnType, getColumnOrder, getPaletteColorsHeadings } from './paletteColorsTableHeadings';

export const ResetButton: FunctionComponent<{ onClick: () => void }> = ({ onClick }) => {
    return (
        <Link onClick={onClick}>
            <Icon $hoverable>
                <UndoIcon />
            </Icon>
        </Link>
    );
};

const ActionsColContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    justify-items: center;
    width: 100%;

    a {
        color: ${(props) => props.theme.app.primary};
    }
`;

const ColorCodeContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
    input {
        max-width: 65px;
        margin-right: 8px;
    }
`;

const HexRgbContainer = styled.div`
    display: flex;
    flex-direction: column;
    input {
        max-width: 120px;
    }
`;

const ColorNameContainer = styled.div`
    display: flex;
    width: 100%;
    input {
        width: 100%;
    }
`;

const PantoneInputContainer = styled.div<{ isZakekeDefault?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    max-width: 100%; /* Reasonable max width for Pantone codes */

    input {
        padding-right: 36px; /* Make space for the icon */
        width: 100%;
    }

    .book-open-icon {
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
        width: 24px;
        height: 24px;
        fill: ${(props) => (props.isZakekeDefault ? props.theme.app.primary_light : props.theme.app.primary)};
        cursor: ${(props) => (props.isZakekeDefault ? 'not-allowed' : 'pointer')};
        transition: fill 0.2s ease;

        &:hover {
            // fill: ${(props) => props.theme.app.secondary};
            opacity: 0.7;
        }
    }
`;

interface PaletteColorsTableProps {
    colors: PaletteColorInput[]; // Accept only the colors array
    onDelete?: (index: number) => void; // Callback for deleting a color
    onAdd?: () => void; // Callback for adding a new color
    onEdit?: (index: number, updatedColor: PaletteColorInput) => void; // Callback for editing a color
    onReset?: (index: number) => void; // Callback for resetting a color
    onPantoneIconClick?: (index: number, color: PaletteColorInput) => void; // Callback for Pantone icon click
    isZakekeDefault?: boolean; // Flag for default palettes
    paletteType: PaletteType; // Type of the palette
    isEditing?: boolean;
}

export const PaletteColorsTable: FC<PaletteColorsTableProps> = ({
    colors,
    onDelete,
    onAdd,
    onEdit,
    onReset,
    onPantoneIconClick,
    isZakekeDefault = false,
    paletteType,
    isEditing
}) => {
    // Create refs for the first row inputs that need focus
    const pantoneInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const cmykInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Focus on the appropriate input when a new row is added (first row, index 0)
    useEffect(() => {
        if (colors.length > 0) {
            const firstColor = colors[0];
            const isEmpty = paletteType === PaletteType.Pantone ? !firstColor.spotColorName && !firstColor.name : !firstColor.cmykHexCode;

            if (isEmpty) {
                if (paletteType === PaletteType.Pantone && pantoneInputRefs.current[0]) {
                    pantoneInputRefs.current[0].focus();
                } else if (paletteType === PaletteType.CMYK && cmykInputRefs.current[0]) {
                    cmykInputRefs.current[0].focus();
                }
            }
        }
    }, [colors.length, paletteType]);

    const updateColor = (index: number, field: string, value: string) => {
        const updatedColors = [...colors];
        let updatedColor = { ...updatedColors[index] };

        if (field === 'r' || field === 'g' || field === 'b') {
            // Update RGB channel and recalculate hex
            const rgb = hexToRgb(updatedColor.rgbHexCode || '#000000');
            const newRgb = { ...rgb, [field]: Number(value) };
            const toHex = (v: number) =>
                Math.max(0, Math.min(255, Number(v)))
                    .toString(16)
                    .padStart(2, '0');
            updatedColor.rgbHexCode = `#${toHex(newRgb.r)}${toHex(newRgb.g)}${toHex(newRgb.b)}`;
        } else if (field === 'c' || field === 'm' || field === 'y' || field === 'k') {
            // Update CMYK channel
            updatedColor.cmykHexCode = unifyCMYK({
                c: field === 'c' ? Number(value) : updatedColor.cmykHexCode ? splitCMYK(updatedColor.cmykHexCode).c : 0,
                m: field === 'm' ? Number(value) : updatedColor.cmykHexCode ? splitCMYK(updatedColor.cmykHexCode).m : 0,
                y: field === 'y' ? Number(value) : updatedColor.cmykHexCode ? splitCMYK(updatedColor.cmykHexCode).y : 0,
                k: field === 'k' ? Number(value) : updatedColor.cmykHexCode ? splitCMYK(updatedColor.cmykHexCode).k : 0
            });
        } else {
            updatedColor = { ...updatedColor, [field]: value };
        }

        onEdit?.(index, updatedColor);
    };

    const safeHexToRgb = (hex: string) => {
        try {
            if (!hex || hex === '') return { r: 0, g: 0, b: 0 };
            if (/^#([0-9A-Fa-f]{3})$/.test(hex)) {
                // Expand shorthand: #678 -> #667788
                const r = hex[1] + hex[1];
                const g = hex[2] + hex[2];
                const b = hex[3] + hex[3];
                return hexToRgb(`#${r}${g}${b}`);
            }

            if (/^#([0-9A-Fa-f]{6})$/.test(hex)) {
                return hexToRgb(hex);
            }
            return hexToRgb(hex);
        } catch {
            return { r: 0, g: 0, b: 0 };
        }
    };

    const safeHexCode = (hex: string) => {
        if (!hex || hex === '') return '#a47864';
        if (/^#([0-9A-Fa-f]{3})$/.test(hex)) {
            // Expand shorthand: #678 -> #667788
            const r = hex[1] + hex[1];
            const g = hex[2] + hex[2];
            const b = hex[3] + hex[3];
            return `#${r}${g}${b}`;
        }
        if (/^#([0-9A-Fa-f]{6})$/.test(hex)) {
            return hex;
        }

        // If not shorthand, return as is
        return '#a47864';
    };

    const unifyCMYK = (cmyk: { c: number; m: number; y: number; k: number }) => {
        return `${cmyk.c}, ${cmyk.m}, ${cmyk.y}, ${cmyk.k}`;
    };

    const splitCMYK = (cmyk: string) => {
        const [c, m, y, k] = cmyk.split(',').map(Number);
        return { c, m, y, k };
    };

    const buildRowCells = (color: PaletteColorInput, index: number) => {
        const isNotEmptyColor =
            (paletteType === PaletteType.Pantone && (color?.name !== '' || color?.spotColorName !== '')) ||
            (paletteType === PaletteType.CMYK && color?.cmykHexCode !== '' && color?.cmykHexCode !== '0, 0, 0, 0');

        // Define all possible cell components
        const allCells: Record<ColumnType, React.ReactNode | [React.ReactNode, { isInput?: boolean }]> = {
            [COLUMN_TYPES.PANTONE_CODE]: [
                <PantoneInputContainer isZakekeDefault={isZakekeDefault}>
                    <Input
                        ref={(el) => (pantoneInputRefs.current[index] = el)}
                        placeholder='Enter Pantone code'
                        type='text'
                        value={color.spotColorName || ''}
                        onChange={(e) => updateColor(index, 'spotColorName', e.currentTarget.value)}
                        disabled={isZakekeDefault}
                    />
                    <BookOpen className='book-open-icon' onClick={() => (!isZakekeDefault ? onPantoneIconClick?.(index, color) : undefined)} />
                </PantoneInputContainer>,
                { isInput: true }
            ],
            [COLUMN_TYPES.NAME]: [
                <>
                    {paletteType === PaletteType.CMYK && (color?.cmykHexCode === '' || color?.cmykHexCode === '0, 0, 0, 0') ? (
                        <ZTooltip position='top' title={T._(`Please enter CMYK code first.`, 'Admin')} addContainer={true}>
                            <ColorNameContainer>
                                <Input
                                    placeholder='Name of the color'
                                    type='text'
                                    value={color.name || ''}
                                    onChange={(e) => updateColor(index, 'name', e.currentTarget.value)}
                                    disabled={isZakekeDefault || (paletteType === PaletteType.CMYK && !color?.cmykHexCode)}
                                />
                            </ColorNameContainer>
                        </ZTooltip>
                    ) : (
                        <Input
                            placeholder='Name of the color'
                            type='text'
                            value={color.name || ''}
                            onChange={(e) => updateColor(index, 'name', e.currentTarget.value)}
                            disabled={isZakekeDefault}
                        />
                    )}
                </>,
                { isInput: true }
            ],
            [COLUMN_TYPES.ARROW]: [
                <IconButtonGrey>
                    <Icon>
                        <ArrowRightSolid />
                    </Icon>
                </IconButtonGrey>,
                { isInput: false }
            ],
            [COLUMN_TYPES.COLOR_PICKER]: [
                <>
                    {(paletteType === PaletteType.Pantone && (color?.name === '' || color?.spotColorName === '')) ||
                    (paletteType === PaletteType.CMYK && (color?.cmykHexCode === '' || color?.cmykHexCode === '0, 0, 0, 0')) ? (
                        <ZTooltip
                            position='top'
                            title={T._(
                                paletteType === PaletteType.Pantone ? `Please enter Pantone Code and Name first.` : `Please enter CMYK code first.`,
                                'Admin'
                            )}
                            addContainer={true}
                        >
                            <ColorSelection
                                colors={[safeHexCode('#ddd')]}
                                showHexCode={false}
                                isForTableUse={true}
                                colorIndex={index}
                                onChange={(index, newColor) => updateColor(index, 'rgbHexCode', newColor)}
                                onClickDisabled={true}
                            />
                        </ZTooltip>
                    ) : (
                        <ColorSelection
                            colors={[safeHexCode(color.rgbHexCode)]}
                            showHexCode={false}
                            isForTableUse={true}
                            colorIndex={index}
                            onChange={(index, newColor) => updateColor(index, 'rgbHexCode', newColor)}
                            onClickDisabled={isZakekeDefault}
                        />
                    )}
                </>,
                { isInput: false }
            ],
            [COLUMN_TYPES.RGB]: [
                <>
                    {(paletteType === PaletteType.Pantone && (color?.name === '' || color?.spotColorName === '')) ||
                    (paletteType === PaletteType.CMYK && (color?.cmykHexCode === '' || color?.cmykHexCode === '0, 0, 0, 0')) ? (
                        <ZTooltip
                            position='top'
                            title={T._(
                                paletteType === PaletteType.Pantone ? `Please enter Pantone Code and Name first.` : `Please enter CMYK code first.`,
                                'Admin'
                            )}
                        >
                            <ColorCodeContainer>
                                <Input placeholder='0' type='text' disabled={true} />
                                <Input placeholder='0' type='text' disabled={true} />
                                <Input placeholder='0' type='text' disabled={true} />
                            </ColorCodeContainer>
                        </ZTooltip>
                    ) : (
                        <ColorCodeContainer>
                            <Input
                                placeholder='0'
                                type='number'
                                value={color ? safeHexToRgb(color.rgbHexCode).r : ''}
                                onChange={(e) => updateColor(index, 'r', e.currentTarget.value)}
                                disabled={isZakekeDefault}
                            />
                            <Input
                                placeholder='0'
                                type='number'
                                value={color ? safeHexToRgb(color.rgbHexCode).g : ''}
                                onChange={(e) => updateColor(index, 'g', e.currentTarget.value)}
                                disabled={isZakekeDefault}
                            />
                            <Input
                                placeholder='0'
                                type='number'
                                value={color ? safeHexToRgb(color.rgbHexCode).b : ''}
                                onChange={(e) => updateColor(index, 'b', e.currentTarget.value)}
                                disabled={isZakekeDefault}
                            />
                        </ColorCodeContainer>
                    )}
                </>,
                { isInput: true }
            ],
            [COLUMN_TYPES.CMYK]: [
                <>
                    <ColorCodeContainer>
                        <Input
                            ref={(el) => (cmykInputRefs.current[index] = el)}
                            placeholder='0'
                            type='number'
                            max={100}
                            min={0}
                            value={color && color.cmykHexCode ? splitCMYK(color.cmykHexCode).c : ''}
                            onChange={(e) => updateColor(index, 'c', e.currentTarget.value)}
                            disabled={isZakekeDefault}
                        />
                        <Input
                            placeholder='0'
                            type='number'
                            max={100}
                            min={0}
                            value={color && color.cmykHexCode ? splitCMYK(color.cmykHexCode).m : ''}
                            onChange={(e) => updateColor(index, 'm', e.currentTarget.value)}
                            disabled={isZakekeDefault}
                        />
                        <Input
                            placeholder='0'
                            type='number'
                            max={100}
                            min={0}
                            value={color && color.cmykHexCode ? splitCMYK(color.cmykHexCode).y : ''}
                            onChange={(e) => updateColor(index, 'y', e.currentTarget.value)}
                            disabled={isZakekeDefault}
                        />
                        <Input
                            placeholder='0'
                            type='number'
                            max={100}
                            min={0}
                            value={color && color.cmykHexCode ? splitCMYK(color.cmykHexCode).k : ''}
                            onChange={(e) => updateColor(index, 'k', e.currentTarget.value)}
                            disabled={isZakekeDefault}
                        />
                    </ColorCodeContainer>
                </>,
                { isInput: true }
            ],
            [COLUMN_TYPES.HEX]: [
                <>
                    {(paletteType === PaletteType.Pantone && (color?.name === '' || color?.spotColorName === '')) ||
                    (paletteType === PaletteType.CMYK && (color?.cmykHexCode === '' || color?.cmykHexCode === '0, 0, 0, 0')) ? (
                        <ZTooltip
                            position='top'
                            title={T._(
                                paletteType === PaletteType.Pantone ? `Please enter Pantone Code and Name first.` : `Please enter CMYK code first.`,
                                'Admin'
                            )}
                        >
                            <HexRgbContainer>
                                <Input placeholder='#000000' type='text' value={''} disabled={true} />
                            </HexRgbContainer>
                        </ZTooltip>
                    ) : (
                        <HexRgbContainer>
                            <Input
                                placeholder='#000000'
                                type='text'
                                value={color.rgbHexCode || ''}
                                onChange={(e) => updateColor(index, 'rgbHexCode', e.currentTarget.value)}
                                disabled={isZakekeDefault}
                            />
                        </HexRgbContainer>
                    )}
                </>,
                { isInput: true }
            ],
            [COLUMN_TYPES.ACTIONS]: [
                <ActionsColContainer>
                    {isNotEmptyColor ? <ResetButton onClick={() => onReset?.(index)} /> : <div style={{ width: '24px' }} />}
                    {colors.length > 1 || isNotEmptyColor ? <DeleteButton onClick={() => onDelete?.(index)} /> : <div style={{ width: '24px' }} />}
                    {0 === index ? <AddButton onClick={() => onAdd?.()} /> : <div style={{ width: '24px' }} />}
                </ActionsColContainer>,
                { isInput: false }
            ]
        };

        // Get the column order for this palette type
        const columnOrder = getColumnOrder(paletteType, isZakekeDefault);

        // Return cells in the correct order
        return columnOrder.map((columnType) => allCells[columnType]);
    };

    return (
        <>
            <Table headings={getPaletteColorsHeadings(paletteType, isZakekeDefault)}>
                {colors.map((color, i) => {
                    return <Row key={i.toString()} cells={buildRowCells(color, i)} />;
                })}
            </Table>
        </>
    );
};
